import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { budgetApi, clientApi } from '@/services/api'
import type { BudgetVariance, BudgetVarianceAccount } from '@/types'
import { TrendingUp } from 'lucide-react'
import toast from 'react-hot-toast'
import { formatCurrency } from '@/utils/currency'
import { Button } from '@/components/ui/button'

function VarianceRow({ row, currency }: { row: BudgetVarianceAccount; currency: string }) {
  const variance = parseFloat(row.variance)
  const varColor = variance >= 0 ? 'text-emerald-600' : 'text-red-500'
  return (
    <tr className="hover:bg-surface-50">
      <td className="px-4 py-2.5 text-[13px] text-[#000A1E]">
        <span className="text-[#CCCED2] text-[13px] mr-1.5">{row.account_code}</span>{row.account_name}
      </td>
      <td className="px-4 py-2.5 text-[13px] text-right text-[#000A1E]">{formatCurrency(row.actual, currency)}</td>
      <td className="px-4 py-2.5 text-[13px] text-right text-[rgba(0,10,30,0.75)]">{formatCurrency(row.budget, currency)}</td>
      <td className={`px-4 py-2.5 text-[13px] text-right font-medium ${varColor}`}>
        {variance >= 0 ? '+' : ''}{formatCurrency(row.variance, currency)}
      </td>
      <td className={`px-4 py-2.5 text-[13px] text-right ${varColor}`}>
        {row.variance_pct ? `${parseFloat(row.variance_pct) >= 0 ? '+' : ''}${row.variance_pct}%` : '—'}
      </td>
    </tr>
  )
}

export default function BudgetVariancePage() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const currentYear = new Date().getFullYear()
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`)
  const [endDate, setEndDate] = useState(`${currentYear}-12-31`)
  const [report, setReport] = useState<BudgetVariance | null>(null)
  const [loading, setLoading] = useState(false)
  const [currency, setCurrency] = useState('USD')
  const [premiumGated, setPremiumGated] = useState(false)

  async function load() {
    if (!clientId) return
    setLoading(true)
    clientApi.get(clientId).then((r) => setCurrency(r.data.client.currency)).catch(() => {})
    try {
      const res = await budgetApi.variance(clientId, startDate, endDate)
      setReport(res.data)
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 402) setPremiumGated(true)
      else toast.error('Failed to load variance report')
    } finally {
      setLoading(false)
    }
  }

  const colHead = 'px-4 py-2.5 text-[13px] font-bold text-[rgba(0,10,30,0.75)] uppercase tracking-wider'

  function SectionRow({ label, actual, budget, variance }: { label: string; actual: string; budget: string; variance: string }) {
    const v = parseFloat(variance)
    return (
      <tr className="bg-surface-50 border-t border-surface-200">
        <td className="px-4 py-2.5 text-[13px] font-bold text-[#000A1E]">{label}</td>
        <td className="px-4 py-2.5 text-[13px] font-bold text-right text-[#000A1E]">{formatCurrency(actual, currency)}</td>
        <td className="px-4 py-2.5 text-[13px] font-bold text-right text-[rgba(0,10,30,0.75)]">{formatCurrency(budget, currency)}</td>
        <td className={`px-4 py-2.5 text-[13px] font-bold text-right ${v >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          {v >= 0 ? '+' : ''}{formatCurrency(variance, currency)}
        </td>
        <td />
      </tr>
    )
  }

  if (premiumGated) {
    return (
      <div className="max-w-sm mx-auto mt-16">
        <div className="card p-10 text-center">
          <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mx-auto mb-4">
            <TrendingUp className="w-6 h-6 text-amber-500" />
          </div>
          <h2 className="text-[21px] font-bold text-[#000A1E] leading-[32px] mb-2">Premium Feature</h2>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-5">
            Budget variance reports require a Premium client. Upgrade in Client Settings &rarr; Plan.
          </p>
          <Button variant="default" onClick={() => navigate(`/clients/${clientId}/settings`)}>
            Upgrade to Premium
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1">
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Budget Variance</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Actual vs budgeted performance</p>
        </div>
      </div>

      <div className="card p-4 mb-5 flex items-end gap-4">
        <div>
          <label className="input-label">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input" />
        </div>
        <div>
          <label className="input-label">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input" />
        </div>
        <Button variant="default" onClick={load} disabled={loading}>
          {loading ? 'Loading...' : 'Run Report'}
        </Button>
      </div>

      {report && (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-surface-100">
              <tr>
                <th className={`${colHead} text-left`}>Account</th>
                <th className={`${colHead} text-right`}>Actual</th>
                <th className={`${colHead} text-right`}>Budget</th>
                <th className={`${colHead} text-right`}>Variance</th>
                <th className={`${colHead} text-right`}>%</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={5} className="px-4 py-1.5 text-[11px] font-bold text-[rgba(0,10,30,0.75)] uppercase tracking-widest bg-surface-50">
                  Revenue
                </td>
              </tr>
              {report.revenue.accounts.map((r) => <VarianceRow key={r.account_id} row={r} currency={currency} />)}
              <SectionRow
                label="Total Revenue"
                actual={report.revenue.actual_total}
                budget={report.revenue.budget_total}
                variance={report.revenue.variance_total}
              />

              <tr>
                <td colSpan={5} className="px-4 py-1.5 text-[11px] font-bold text-[rgba(0,10,30,0.75)] uppercase tracking-widest bg-surface-50">
                  Expenses
                </td>
              </tr>
              {report.expenses.accounts.map((r) => <VarianceRow key={r.account_id} row={r} currency={currency} />)}
              <SectionRow
                label="Total Expenses"
                actual={report.expenses.actual_total}
                budget={report.expenses.budget_total}
                variance={report.expenses.variance_total}
              />

              <tr className="border-t-2 border-surface-200 bg-[#0078C8]/10">
                <td className="px-4 py-3 text-[13px] font-bold text-[#000A1E]">Net Profit</td>
                <td className="px-4 py-3 text-[13px] font-bold text-right text-[#000A1E]">{formatCurrency(report.net_actual, currency)}</td>
                <td className="px-4 py-3 text-[13px] font-bold text-right text-[rgba(0,10,30,0.75)]">{formatCurrency(report.net_budget, currency)}</td>
                <td className={`px-4 py-3 text-[13px] font-bold text-right ${parseFloat(report.net_variance) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {parseFloat(report.net_variance) >= 0 ? '+' : ''}{formatCurrency(report.net_variance, currency)}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
